/// Tidal streams — the second half of tides (after height). Tidal
/// stream atlas + diamond lookups, cross-track error, ETA-under-tide
/// calculations. Day Skipper + Coastal Skipper material.
///
/// Three domains:
///
///   1. Tidal stream sources — atlas + chart diamond + electronic.
///   2. ETA under tide — combining DST with tidal stream gates.
///   3. Cross-track error (XTE) — the GPS-reported quantity + what
///      it means.

// ───────────────────────────────────────────────────────────────────
// 1. Tidal stream sources
// ───────────────────────────────────────────────────────────────────

export interface TidalStreamSource {
  id: "atlas" | "diamond" | "electronic" | "almanac-table";
  name: string;
  /// One-line gist.
  summary: string;
  /// How to read it.
  howToUse: string;
  /// Accuracy + applicability.
  applicability: string;
}

export const TIDAL_STREAM_SOURCES: readonly TidalStreamSource[] = Object.freeze([
  {
    id: "atlas",
    name: "Tidal Stream Atlas",
    summary:
      "Series of 13 charts showing tidal stream direction + speed at each hour from HW-6 to HW+6 relative to a standard port (typically Dover for UK).",
    howToUse:
      "Find the hour you care about. The arrows on each chart show direction (where the stream is going TO) + speed at neap (top number, italic) + spring (bottom number, bold) rates. For times between hours, interpolate.",
    applicability:
      "UK + European waters. Covers the broad picture of tidal streams — good for passage planning, not for precise pilotage.",
  },
  {
    id: "diamond",
    name: "Tidal diamonds (chart-printed)",
    summary:
      "Lettered diamonds (A, B, C, ...) on a chart with a table showing hourly tidal stream direction + speed at that location.",
    howToUse:
      "Find the diamond letter closest to your position. Look up the time (typically HW±x of a standard port) in the printed table. Read direction + speed for that hour.",
    applicability:
      "Detailed local resolution — typical UK chart has 10-20 diamonds covering its area. Most accurate source for shore-based passage planning.",
  },
  {
    id: "electronic",
    name: "Electronic chart-plotter overlays",
    summary:
      "Modern chart-plotters overlay live or predicted tidal stream arrows directly on the chart at the time selected.",
    howToUse:
      "Switch on the 'tidal stream' overlay. Arrows display predicted direction + speed at the displayed time. Some plotters also show the resulting current vector applied to your GPS-track.",
    applicability:
      "Convenient + visual. Accuracy depends on the database the plotter uses. Always cross-check with the chart's diamonds for an important fix.",
  },
  {
    id: "almanac-table",
    name: "Almanac tide-stream tables",
    summary:
      "Tabulated direction + speed for major UK passages (English Channel, Solent, Bristol Channel, etc.) at hourly intervals relative to a reference port.",
    howToUse:
      "Find your passage in the index. Look up the hour relative to the reference port. Stream direction + speed printed.",
    applicability:
      "Quick reference for well-known UK passages. Good for passage planning sanity checks. Lookups are slower than reading a chart diamond.",
  },
]);

// ───────────────────────────────────────────────────────────────────
// 2. ETA under tide — calculating arrival with streams
// ───────────────────────────────────────────────────────────────────

export interface EtaInputs {
  /// Distance to destination, in nautical miles.
  distanceNm: number;
  /// Cruising speed through the water (i.e. before stream is applied),
  /// in knots.
  speedKt: number;
  /// Net favourable stream over the passage, in knots. Positive =
  /// pushing toward destination; negative = punching against.
  /// If stream varies during the passage, use an average.
  netStreamKt: number;
}

export interface EtaResult {
  /// Expected speed over the ground, in knots.
  sogKt: number;
  /// Estimated time of arrival, in hours from now.
  etaHours: number;
}

/// Compute ETA under tide — distance + speed through water + net
/// favourable stream → SOG + ETA. Simple linear arithmetic; assumes
/// stream is constant or averaged.
export function etaUnderTide(input: EtaInputs): EtaResult {
  const sog = Math.max(0.01, input.speedKt + input.netStreamKt);
  return {
    sogKt: sog,
    etaHours: input.distanceNm / sog,
  };
}

// ───────────────────────────────────────────────────────────────────
// 3. Cross-track error (XTE)
// ───────────────────────────────────────────────────────────────────

export const XTE_NOTES: readonly string[] = Object.freeze([
  "Cross-track error (XTE) is the perpendicular distance from your current position to your intended track line. Reported by GPS in nautical miles or metres.",
  "POSITIVE XTE = right of track (you've drifted starboard); NEGATIVE = left of track. Some plotters use 'L' and 'R' directly.",
  "Caused by tidal stream pushing you sideways + wind drift (leeway) + helmsman error.",
  "Acceptable XTE depends on the passage. Open ocean: 0.5–1 nm is fine. Coastal pilotage: keep within 0.1 nm of track to avoid hazards.",
  "ACTION when XTE grows: steer slightly into the cross-current direction to correct. Don't 'over-steer' — small course adjustments add up.",
  "Auto-pilots in 'NAV' or 'TRACK' mode follow the track + maintain low XTE; auto-pilots in 'HEADING' mode just steer the compass course, ignoring XTE.",
  "On a long offshore leg, set the chart-plotter's XTE alarm at 0.5 nm so you're alerted to significant drift.",
  "XTE doesn't tell you DOWN-track error (whether you've reached the waypoint yet). Use the GPS distance-to-waypoint reading for that.",
]);
